import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {createRef, ReactNode, RefObject} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import {InputText} from "primereact/inputtext";
import Parameter from "../../parameter/Parameter";
import PortalDocument, {PortalDocumentInterface} from "../../../document/PortalDocument";
import PortalDocumentManager, {PortalDocumentManagerGlobals} from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import RenameDialogFields from "../../field/document/RenameDialogFields";
import FileHelper from "../../../../shared/helper/FileHelper";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {PortalRename} from "../../../../shared/models/PortalRename";
import {
	DocumentFile,
	Functions,
	Information,
	SignatureEditProfile
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Helper from "../../../../shared/components/Helper";
import Translate from "../../../../shared/translation/Translate";
import Info from "../../../../shared/components/Info";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface RenameDialogProperties extends PortalDialogProperties<PortalRename>, PortalDocumentManagerGlobals {
}

export class RenameDialogDefinition extends PortalDialog<PortalRename, RenameDialogProperties,
	PortalDialogState<PortalRename>> {
	private renameInput: RefObject<HTMLInputElement>;

	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			fieldErrors: new Map<RenameDialogFields, string>()
		};

		this.renameInput = createRef<HTMLInputElement>();
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.functionsStatus?.renameDocument, Functions.getRenameDocumentDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		for (let selectedDocument of selectedDocuments) {
			if (selectedDocument.isLocked()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1;
	}

	public renderDialog(): ReactNode {
		let parameter: PortalRename | undefined = this.state.parameter?.data;
		let selected: PortalDocumentInterface | undefined = this.props.documentSelection.get().pop();
		let errors: Map<RenameDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(selected)) {
			return null;
		}

		let classList: Array<string> = ["rename-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.rename.general"} namespace={"help"} markdown={true}/>
					</Helper>
					<fieldset className="p-col-12 p-grid"
							  hidden={!this.state.fields?.contains(RenameDialogFields.FILENAME)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-filename">
							<Translate {...this.props} data="rename.filename" namespace="dialog"/>
							<Info {...this.props} data="rename.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<div className={
								"p-inputgroup" + (errors.has(RenameDialogFields.FILENAME) ? " p-invalid" : "")
							}>
								<InputText id="control-filename" autoFocus={true} ref={this.renameInput}
										   className={errors.has(RenameDialogFields.FILENAME) ? "p-invalid" : ""}
										   value={FunctionHelper.applyValue(
											   parameter?.fileName, PortalRename.getFileNameDefault()
										   )}
										   tooltip={errors.has(RenameDialogFields.FILENAME) ? Translate.value(
											   errors.get(RenameDialogFields.FILENAME)!, {ns: "message"}
										   ) : ""}
										   onInput={(e) => this.onChange(
											   RenameDialogFields.FILENAME, e.currentTarget.value
										   )}/>
								<span className="p-inputgroup-addon">{"." + selected!.extension}</span>
							</div>
							{this.renderFieldError(errors.has(RenameDialogFields.FILENAME) ? Translate.value(
								errors.get(RenameDialogFields.FILENAME)!, {ns: "message"}
							) : "")}
						</div>
					</fieldset>
				</div>
			</Dialog>
		);
	}

	private onChange(field: RenameDialogFields, value: any): void {
		let parameter: PortalRename = this.state.parameter!.data;
		let errors: Map<RenameDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case RenameDialogFields.FILENAME:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!FileHelper.isFilename(value)) {
					errors.set(field, "errors.fields.filename");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.fileName = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter,
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-rename small"}/>
					<Translate {...this.props} data="rename.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let selection: Array<PortalDocument> = this.props.context.documentManager.getSelection();
		let parameter: PortalRename | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		parameter!.fileName = FunctionHelper.applyValue(
			selection[0]?.getDocumentFile()?.fileName, DocumentFile.getFileNameDefault()
		);

		if (FunctionHelper.exists(this.renameInput.current)) {
			this.renameInput.current!.select();
		}

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone()
		})
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let operationData: PortalRename = this.state.parameter!.data.clone();
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await documentManager.renameDocument(portalDocument.getSourceDocumentId()!, operationData.fileName);
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.document.rename", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
		}

		this.hide();
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.RENAME;
	}

	protected getDefaultParameter(): Parameter<PortalRename> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}

	protected isEditPasswordNeeded(): boolean {
		return false; // Don't check for password
	}

	protected async resetParameter(): Promise<void> {
		await super.resetParameter();

		if (FunctionHelper.exists(this.renameInput.current)) {
			this.renameInput.current!.select();
		}
	}
}

const RenameDialog = React.memo(RenameDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<PortalRename>>,
		nextProps: Readonly<PortalDialogProperties<PortalRename>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default RenameDialog;